/* eslint-disable @typescript-eslint/no-explicit-any */
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import AddOrUpdateModel from "../../../GenericComponents/AddOrUpdateModel";
import { Vocabulary } from "../../../../Utils/Vocabulary";
import { InventoryModel, LocationModel } from "../../../../Utils/Models";
import { useContext, useEffect, useRef, useState } from "react";
import { Autocomplete, Checkbox, Grid, IconButton, Paper } from "@mui/material";
import {
  handleChangeAutocomplete,
  onChangeTextField,
} from "../../../../Utils/Utils";
import useCustomMemo from "../../../GenericComponents/useCustomMemo";
import { Add } from "@mui/icons-material";
import LocationMainPage from "../../../Locations/LocationMainPage";
import { updateData } from "../../../../Services/updateData";
import { urlEnum } from "../../../../Utils/UrlEnum";
import { CacheWebsocketContext } from "../../../../Contexts/WebsocketContext";
import { postData } from "../../../../Services/postData";

type InventoryProps = {
  editInventory: InventoryModel | null;
  open: boolean;
  addOrUpdateInventory: (inventory: InventoryModel | null) => void;
  onClose: () => void;
};

export default function Inventory(props: InventoryProps) {
  const { editInventory, open, addOrUpdateInventory, onClose } = props;
  const ref: any = useRef();
  const cache = useCustomMemo();
  const locations = cache.locations || [];
  const [inventory, setInventory] = useState<InventoryModel>(
    new InventoryModel()
  );
  const [pressOnSubmit, setPressOnSubmit] = useState(false);
  const [checked, setChecked] = useState(false);
  const [location, setLocation] = useState(false);
  const websocketContext = useContext(CacheWebsocketContext);
  

  /**
   *
   */
  useEffect(() => {
    if (editInventory) {
      setInventory(editInventory);
    } else {
      setInventory(new InventoryModel());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editInventory]);

  /**
   *
   */
  useEffect(() => {
    if (pressOnSubmit) {
      ref.current.submit();
      //reset the value
      handleChangePressOnSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressOnSubmit]);

  /**
   *
   */
  function handleChangePressOnSubmit() {
    setPressOnSubmit(!pressOnSubmit);
  }

  /**
   * Used to fetch the value of the checkbox
   * I had to use this beause the inventory could be 'undefined' at first
   * Maybe use a better method
   */
  useEffect(() => {
    if (inventory.hasRepairItems) {
      setChecked(true);
    } else {
      setChecked(false);
    }
  }, [inventory]);

  function AddButtonForAutocomplete({ children, fieldName, ...other }: any) {
      return (
        <Paper {...other}>
          <div style={{ position: "absolute", right: -30, top: -32 }}>
            <IconButton
              onMouseDown={(e) => {
                e.preventDefault();
                setLocation(true);
              }}
              style={{
                background: "#18cc9c",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                padding: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Add style={{ verticalAlign: "middle" }} />
            </IconButton>
          </div>
          {children}
        </Paper>
      );
    }

  function addOrUpdateLocation(location: LocationModel | null) {
      if (location?._id) {
        updateData(`${urlEnum.locations}/${location._id}`, location)
          .then((res) => {
            if (res) {
              websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
              setLocation(false);
            }
          })
          .catch((err) => {
            setLocation(false);
          });
      } else {
        postData(urlEnum.locations, location)
          .then((res: any) => {
            if (res) {
              websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
              setLocation(false);
            }
          })
          .catch((err) => {
            setLocation(false);
          });
      }
    }

  return (
    <ValidatorForm ref={ref} onSubmit={() => addOrUpdateInventory(inventory)}>
      <AddOrUpdateModel
        open={open}
        title={Vocabulary.inventory}
        onClose={onClose}
        isSubmit={true}
        addOrUpdateItem={() => null}
        handleChangePressOnSubmit={handleChangePressOnSubmit}
      > 
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              id="name"
              name="name"
              label={Vocabulary.name}
              fullWidth
              variant="outlined"
              value={inventory.name}
              onChange={(e: any) =>
                setInventory(onChangeTextField(e, inventory))
              }
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextValidator
              size="small"
              id="description"
              name="description"
              label={Vocabulary.description}
              fullWidth
              variant="outlined"
              value={inventory.description}
              onChange={(e: any) =>
                setInventory(onChangeTextField(e, inventory))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Autocomplete
              size="small"
              id="location"
              freeSolo={false}
              disablePortal
              options={locations}
              value={inventory.location || null}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option: any, value: any) =>
                option.name === value.name
              }
              onChange={(event: any, value: any) => {
                setInventory(
                  handleChangeAutocomplete(event, value, inventory, "location")
                );
              }}
              PaperComponent={({ children, ...other }) =>
                AddButtonForAutocomplete({
                  children,
                  fieldName: "type",
                  ...other,
                })
              }
              renderInput={(params: any) => (
                <TextValidator
                  {...params}
                  size="small"
                  variant="outlined"
                  value={inventory.location || null}
                  label={Vocabulary.location}
                  validators={["required"]}
                  errorMessages={[Vocabulary.requiredField]}
                />
              )}
              style={{ minWidth: 250 }}
            />
            {location && 
            <LocationMainPage
            editLocation={null}
            open={location}
            addOrUpdateLocation={(location) => addOrUpdateLocation(location)}
            onClose={() => setLocation(false)}
            />}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Checkbox
              checked={checked}
              onChange={(event: any) => {
                setInventory({
                  ...inventory,
                  hasRepairItems: event.target.checked,
                });
              }}
            />
            {Vocabulary.hasRepairItems}
          </Grid>
        </Grid>
      </AddOrUpdateModel>
    </ValidatorForm>
  );
}
